import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';
import { getCategories } from '../helpers/categories';
import { getEstablishments } from '../helpers/establishments';
import { getReviews } from '../helpers/reviews';
import { API_BASE_URL } from '../helpers/api';
import '../css/Categories.css';
import { truncateText } from '../hooks/tools';

const Categories = () => {
  usePageTitle('Catégories');
  const [activeCategory, setActiveCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [categoriesData, establishmentsData, reviewsData] = await Promise.all([
        getCategories(),
        getEstablishments(),
        getReviews()
      ]);

      // Process reviews data
      const reviewStats = {};
      reviewsData?.forEach(review => {
        if (!review?.establishment?.documentId) return;
        const estId = review.establishment.documentId;
        if (!reviewStats[estId]) {
          reviewStats[estId] = {
            count: 0,
            sum: 0
          };
        }
        reviewStats[estId].count++;
        reviewStats[estId].sum += review.rating || 0;
      });

      // Process establishments
      const processedEstablishments = establishmentsData.map(est => ({
        ...est,
        reviewStats: reviewStats[est.documentId] 
          ? {
              count: reviewStats[est.documentId].count,
              average: parseFloat((reviewStats[est.documentId].sum / 
                reviewStats[est.documentId].count).toFixed(1))
            }
          : { count: 0, average: 0 }
      }));

      // Process categories with establishment counts
      const processedCategories = categoriesData.map(cat => ({
        ...cat,
        count: processedEstablishments.filter(est => est.category.id === cat.id).length
      }));

      setCategories(processedCategories);
      setEstablishments(processedEstablishments);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const getDisplayEstablishments = () => {
    if (activeCategory === 'all') {
      return establishments;
    }
    return establishments.filter(est => est.category.id === parseInt(activeCategory));
  };

  if (isLoading) {
    return <div className="loading">Chargement...</div>;
  }

  return (
    <div className="categories-page">
      <div className="categories-hero">
        <div className="container">
          <h1>Catégories</h1>
          <p>Découvrez tous les établissements par catégorie</p>
        </div>
      </div>

      <div className="container">
        <div className="categories-filters">
          <button 
            className={`category-filter ${activeCategory === 'all' ? 'active' : ''}`}
            onClick={() => setActiveCategory('all')}
          >
            Tout voir
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              className={`category-filter ${activeCategory === category.id.toString() ? 'active' : ''}`}
              onClick={() => setActiveCategory(category.id.toString())}
            >
              <i className={`bi ${category.icon}`} />
              <span>{category.name}</span>
              <span className="count">{category.count}</span>
            </button>
          ))}
        </div>

        <div className="establishments-grid">
          {getDisplayEstablishments().map((establishment) => (
            <Link 
              to={`/spot/${establishment.documentId}`}
              key={establishment.id}
              className="establishment-card"
            >
              <div className="card-image">
                <img 
                  src={establishment.images[0]?.url ? API_BASE_URL + establishment.images[0].url : '/placeholder.jpg'} 
                  alt={establishment.name} 
                />
                <span className="price-badge">{establishment.price}</span>
              </div>
              <div className="card-content">
                <h3>{establishment.name}</h3>
                <p>{truncateText(establishment.description, 120)}</p>
                <div className="card-meta">
                  <span className="rating">
                    <Star size={16} fill="#ffc107" color="#ffc107" />
                    {establishment.reviewStats.average}
                    <small>({establishment.reviewStats.count})</small>
                  </span>
                  <div className="tags">
                    {establishment.tags.slice(0, 3).map((tag, index) => (
                      <span key={index} className="tag">{tag.name}</span>
                    ))}
                    {establishment.tags.length > 3 && (
                      <span className="tag-more">+{establishment.tags.length - 3}</span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Categories;